import { WORKFLOW_TAGS } from '../constants/workflow-tags';

/**
 * Valid workflow category values derived from WORKFLOW_TAGS
 */
const VALID_CATEGORIES = Object.values(WORKFLOW_TAGS).map(tag => tag.value.toLowerCase());

/**
 * Offset from 'space' segment to category segment in URL structure:
 * /reports-and-analysis/space/:spaceId/:category/:workflowId/...
 */
const CATEGORY_OFFSET_FROM_SPACE = 2;

/**
 * Extracts the workflow category from a URL pathname.
 * 
 * @param pathname - The URL pathname (e.g., '/reports-and-analysis/space/123/mfr/456/report/789')
 * @returns The category in uppercase (e.g., 'MFR', 'PPM') or null if not found
 * 
 * @example
 * extractCategoryFromPathname('/reports-and-analysis/space/123/mfr/456/report/789') // 'MFR'
 * extractCategoryFromPathname('/reports-and-analysis/space/123/ppm/456/run/789') // 'PPM'
 * extractCategoryFromPathname('/dashboard') // null
 */
export const extractCategoryFromPathname = (pathname: string): string | null => {
  if (!pathname) return null;

  const segments = pathname.toLowerCase().split('/');
  const spaceIndex = segments.findIndex(segment => segment === 'space');

  const categoryIndex = spaceIndex + CATEGORY_OFFSET_FROM_SPACE;
  if (spaceIndex === -1 || categoryIndex >= segments.length) {
    return null;
  }

  const categorySegment = segments[categoryIndex];

  if (VALID_CATEGORIES.includes(categorySegment)) {
    return categorySegment.toUpperCase();
  }

  return null;
};

/**
 * Builds the corrected pathname by replacing the category segment at its expected position.
 * Exported for use by useWorkflowCategoryValidation hook.
 * 
 * @param pathname - The original URL pathname
 * @param newCategory - The correct category to replace with
 * @returns The corrected pathname
 */
export const buildCorrectedPath = (pathname: string, newCategory: string): string => {
  const segments = pathname.split('/');
  const spaceIndex = segments.findIndex(segment => segment.toLowerCase() === 'space');

  const categoryIndex = spaceIndex + CATEGORY_OFFSET_FROM_SPACE;
  if (spaceIndex === -1 || categoryIndex >= segments.length) {
    return pathname;
  }

  segments[categoryIndex] = newCategory.toLowerCase();
  return segments.join('/');
};
